import React, { useState, useEffect } from 'react';
import MultiRentalListingSection from './listings/MultiRentalListingSection';
import AgreementDetailsSection from './listings/AgreementDetailsSection';
import { getRentalListings } from '../services/propertyService';
import { getRentalAccount } from '../services/rentalAccountService';

const PropertyListings = ({ property, agencyCurrency, initialListingId = null }) => {
  // Shared state for agreement and account data
  const [selectedAgreement, setSelectedAgreement] = useState(null);
  const [selectedListing, setSelectedListing] = useState(null);
  const [listingsSubTab, setListingsSubTab] = useState('payments');
  const [refreshTrigger, setRefreshTrigger] = useState(0); // Trigger refresh without remounting

  // Check if property is multi-rental using the string value from API
  const isMultiRental = property.property_type === 'multi_rental';

  console.log('[PropertyListings] Property type check:', {
    propertyType: property.property_type,
    isMultiRental: isMultiRental
  });

  // Data fetching for single rentals (moved from SingleRentalListingSection)
  useEffect(() => {
    const fetchSingleRentalData = async () => {
      if (isMultiRental || !property?.id) return;

      try {
        const agencyId = sessionStorage.getItem('agencyId');
        if (!agencyId) {
          throw new Error('No agency ID found in session storage');
        }

        console.log('[PropertyListings] Fetching rental listing for single property:', property.id);
        const listingsData = await getRentalListings(agencyId, property.id);

        const safeListings = Array.isArray(listingsData) ? listingsData : [];
        const listing = safeListings.length > 0 ? safeListings[0] : null;

        if (listing) {
          setSelectedListing(listing);

          // Handle agreement selection
          if (listing.current_tenancy?.rental_agreement) {
            setSelectedAgreement(listing.current_tenancy.rental_agreement);

            // Load rental account if tenancy exists
            if (listing.current_tenancy.tenancy_ledger_id) {
              try {
                await getRentalAccount(listing.current_tenancy.tenancy_ledger_id);
              } catch (error) {
                console.error('Error fetching rental account:', error);
              }
            }
          } else {
            setSelectedAgreement(null);
          }
        } else {
          setSelectedListing(null);
          setSelectedAgreement(null);
        }
      } catch (error) {
        console.error('[PropertyListings] Error fetching rental listing:', error);
      }
    };

    fetchSingleRentalData();
  }, [property, isMultiRental]);



  // Handle agreement updates - refresh listing data from server
  const handleAgreementUpdate = async (updatedAgreement) => {
    try {
      const agencyId = sessionStorage.getItem('agencyId');
      if (!agencyId || !property?.id) return;

      console.log('[PropertyListings] Refreshing listing data after agreement update');

      // Refresh the rental listings from server
      const listingsData = await getRentalListings(agencyId, property.id);
      const safeListings = Array.isArray(listingsData) ? listingsData : [];

      if (isMultiRental) {
        // For multi-rental, find the current selected listing and update it
        if (selectedListing) {
          const updatedListing = safeListings.find(l => l.id === selectedListing.id);
          if (updatedListing) {
            setSelectedListing(updatedListing);

            // Handle agreement selection with fresh data
            if (updatedListing.current_tenancy?.rental_agreement) {
              setSelectedAgreement(updatedListing.current_tenancy.rental_agreement);
            } else {
              setSelectedAgreement(null);
            }
          }
        }
        // Trigger refresh without remounting
        setRefreshTrigger(prev => prev + 1);
      } else {
        // For single rental, refresh the data directly
        const listing = safeListings.length > 0 ? safeListings[0] : null;

        if (listing) {
          setSelectedListing(listing);

          // Handle agreement selection with fresh data
          if (listing.current_tenancy?.rental_agreement) {
            setSelectedAgreement(listing.current_tenancy.rental_agreement);

            // Load rental account if tenancy exists
            if (listing.current_tenancy.tenancy_ledger_id) {
              try {
                await getRentalAccount(listing.current_tenancy.tenancy_ledger_id);
              } catch (error) {
                console.error('Error fetching rental account:', error);
              }
            }
          } else {
            setSelectedAgreement(null);
          }
        } else {
          setSelectedListing(null);
          setSelectedAgreement(null);
        }
      }
    } catch (error) {
      console.error('[PropertyListings] Error refreshing agreement data:', error);
    }
  };

  // Callback handlers for child components
  const handleAgreementSelect = (agreement) => {
    setSelectedAgreement(agreement);
  };

  const handleListingSelect = (listing) => {
    setSelectedListing(listing);
  };

  return (
    <div>
      {/* Only show listing section for multi-rental properties */}
      {isMultiRental && (
        <MultiRentalListingSection
          property={property}
          refreshTrigger={refreshTrigger}
          selectedListingId={selectedListing?.id}
          initialListingId={initialListingId}
          onAgreementSelect={handleAgreementSelect}
          onListingSelect={handleListingSelect}
          onAccountLoad={() => {}} // Keep for compatibility but no longer used
        />
      )}

      {/* Common agreement display and tabs */}
      <AgreementDetailsSection
        selectedAgreement={selectedAgreement}
        selectedListing={selectedListing}
        agencyCurrency={agencyCurrency}
        property={property}
        activeTab={listingsSubTab}
        onTabChange={setListingsSubTab}
        onAgreementUpdate={handleAgreementUpdate}
      />
    </div>
  );
};

export default PropertyListings;
